/* ==========================================================================
   rn-config-loader.js

   Loads the configurator on demand instead of rendering it into the initial
   page HTML. Used by the Renegade, Helix, Ballista, Rebel and Pandora
   landing templates.

   NOTHING LOADS UNTIL SOMEBODY CLICKS.
   An earlier version pre-warmed the fetch once the visitor scrolled past the
   first screen. That was a mistake: rendering crawlers routinely auto-scroll
   or use a very tall viewport to trigger lazy-loaded content, so the
   component catalogue was being handed straight back to the crawler this
   exists to hide it from. The pre-warm is gone. The first click now costs
   about a second of loading, which is the correct trade.

   DEEP LINKS NEED A REAL GESTURE, NOT A URL.
   /pages/helix?cpu=amd-cpu-7000#configure is a crawlable link. Opening the
   configurator just because that URL was requested means a rendering crawler
   following the link gets the whole catalogue. So the click handler records
   intent in sessionStorage, and only an arrival carrying that intent opens
   the configurator automatically. Crawlers do not carry sessionStorage
   between requests, so they never trigger it; a person clicking through from
   another page does.

   Trade-off worth knowing: a ?cpu= link pasted fresh into the address bar or
   opened from an email lands on the editorial page rather than the
   configurator. The entry points are right there, one click away.

   FOUR THINGS THIS HAS TO GET RIGHT, all found by auditing the snippet:

   1. Scripts inserted as HTML never execute. Each one is recreated as a
      fresh element. External scripts are awaited so order is preserved.

   2. The fragment loads jQuery 3.6.0 from code.jquery.com in the middle of
      its markup. Running that again would swap window.jQuery for a fresh
      instance and orphan every handler already bound, so it is skipped.

   3. Most configurator handlers are directly bound rather than delegated.
      They attach to whatever exists when the script runs, so the markup must
      be in the DOM first. It is.

   4. Running twice would double-bind handlers and double-count prices. The
      state below guarantees one execution, and concurrent callers share the
      same promise.
   ========================================================================== */

(function () {
  'use strict';

  var mount = document.getElementById('configure');
  if (!mount || !mount.hasAttribute('data-rn-config')) return;

  var VIEW = mount.getAttribute('data-rn-config-view') || 'confonly';
  var INTENT = 'rn-config-intent';
  var state = 'idle';   /* idle | loading | ready | failed */
  var pending = null;

  /* lp-preselect puts an lp-jump class on <html> the moment it sees a cpu
     parameter, which hides #overview and reveals #configure for up to eight
     seconds while it hunts for a Generation button. With the configurator no
     longer in the page that would blank the landing page for nothing, so the
     class comes straight back off. Deep links are this loader's job now. */
  document.documentElement.classList.remove('lp-jump');

  function store(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }
  function read(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function clear(key) {
    try { window.sessionStorage.removeItem(key); } catch (e) { /* private mode */ }
  }

  /* Built from the current URL so theme previews keep working: the
     preview_theme_id parameter has to survive, or the fetch would come back
     from the published theme instead of the one being previewed. */
  function fragmentUrl() {
    var url = new URL(window.location.href);
    url.searchParams.set('view', VIEW);
    url.searchParams.delete('cpu');
    url.searchParams.delete('mb');
    url.hash = '';
    return url.toString();
  }

  function fullPageUrl() {
    var url = new URL(window.location.href);
    url.searchParams.delete('view');
    return url.toString();
  }

  function setBusy(busy) {
    mount.setAttribute('aria-busy', busy ? 'true' : 'false');
    document.documentElement.classList.toggle('rn-config-loading', busy);
  }

  function isJquery(src) {
    return /jquery/i.test(src || '');
  }

  function runScripts(scripts) {
    return scripts.reduce(function (chain, original) {
      return chain.then(function () {
        var src = original.getAttribute('src');
        if (src && isJquery(src)) return;

        return new Promise(function (resolve) {
          var s = document.createElement('script');
          for (var i = 0; i < original.attributes.length; i++) {
            s.setAttribute(original.attributes[i].name, original.attributes[i].value);
          }
          if (src) {
            s.onload = resolve;
            s.onerror = resolve;  /* never stall the chain */
            document.body.appendChild(s);
          } else {
            s.text = original.textContent;
            document.body.appendChild(s);
            resolve();
          }
        });
      });
    }, Promise.resolve());
  }

  function load() {
    if (state === 'ready') return Promise.resolve();
    if (pending) return pending;

    state = 'loading';
    setBusy(true);

    pending = fetch(fragmentUrl(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Configurator fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fragment = doc.querySelector('[data-rn-config-fragment]') || doc.body;

        /* The snippet carries its own span#configure, which would be a second
           element with that id once injected. */
        var dupe = fragment.querySelector('#configure');
        if (dupe) { dupe.id = 'configure-inner'; }

        var scripts = Array.prototype.slice.call(fragment.querySelectorAll('script'));
        scripts.forEach(function (s) { s.parentNode.removeChild(s); });

        mount.appendChild(fragment);   /* markup first, scripts second */
        return runScripts(scripts);
      })
      .then(function () {
        state = 'ready';
        setBusy(false);
        mount.dispatchEvent(new CustomEvent('rn:config-ready', { bubbles: true }));
      })
      .catch(function (err) {
        state = 'failed';
        pending = null;   /* allow a retry on the next click */
        setBusy(false);
        if (window.console) { console.error('[rn-config]', err); }
        window.location.href = fullPageUrl();
      });

    return pending;
  }

  function reveal() {
    return load().then(function () {
      mount.classList.remove('hidden');
      var sidebar = document.querySelector('.float-sidebar');
      if (sidebar) { sidebar.classList.remove('hidden'); }
      var overview = document.getElementById('overview');
      if (overview) { overview.classList.add('hidden'); }
      window.scrollTo(0, 0);
    });
  }

  /* Delegated, so it covers buttons that arrive later and anything the theme
     renders itself. Any link to #configure counts as a request for it. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest
      ? event.target.closest('a[href*="#configure"], a[href*="cpu="], [data-rn-config-open]')
      : null;
    if (!link) return;

    var href = link.getAttribute('href') || '';
    var match = href.match(/[?&]cpu=([a-z0-9-]+)/i);

    /* If the link points at another page, record the intent and let the
       navigation happen — the loader on that page picks it up. */
    var sameDocument = href.indexOf('#') === 0 ||
                       href.indexOf(window.location.pathname) !== -1;
    if (!sameDocument) {
      if (match) { store(INTENT, match[1]); }
      return;
    }

    event.preventDefault();

    reveal().then(function () {
      if (!match) return;
      clickGeneration(match[1]);
    });
  });

  function clickGeneration(handle) {
    var needle = String.fromCharCode(39) + handle + String.fromCharCode(39);
    var all = document.querySelectorAll('[onclick]');
    for (var i = 0; i < all.length; i++) {
      if ((all[i].getAttribute('onclick') || '').indexOf(needle) !== -1) {
        all[i].click();
        return;
      }
    }
  }

  /* Arrival with a cpu parameter opens the configurator ONLY when the intent
     flag from a real click is present. See the note at the top of this file. */
  (function gatedArrival() {
    var params = new URLSearchParams(window.location.search);
    var cpu = params.get('cpu');
    if (!cpu) return;

    var intent = read(INTENT);
    if (!intent) return;   /* a crawler, a bookmark, a pasted link */

    clear(INTENT);
    reveal().then(function () { clickGeneration(cpu); });
  })();
})();
